import LegalPageLayout from '../../components/feature/LegalPageLayout';

const SITE = 'https://blueglobemedia.com';
const EMAIL = 'info@blueglobemedia.com';

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      badge="Legal"
      title="Privacy Policy"
      subtitle="This policy explains how Blue Globe Media collects, uses, and protects your information when you visit our website or use our services."
      lastUpdated="April 4, 2026"
      seo={{
        title: 'Privacy Policy | Blue Globe Media',
        description: 'Read the Blue Globe Media privacy policy. Learn how we collect, use, store, and protect your personal information when you use our website and digital marketing services.',
        keywords: 'privacy policy, Blue Globe Media privacy, data protection, personal information',
        canonicalUrl: `${SITE}/privacy-policy`,
      }}
      sections={[
        {
          title: '1. Who We Are',
          content: (
            <>
              <p>
                Blue Globe Media (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operates the website{' '}
                <a href={SITE} className="text-brand hover:underline">{SITE}</a> and provides digital
                marketing services including SEO, performance marketing, social media management, media buying,
                paid advertising, branding, and website development.
              </p>
              <p>
                If you have questions about this Privacy Policy, contact us at{' '}
                <a href={`mailto:${EMAIL}`} className="text-brand hover:underline">{EMAIL}</a>.
              </p>
            </>
          ),
        },
        {
          title: '2. Information We Collect',
          content: (
            <>
              <p>We may collect the following types of information:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong className="text-gray-800">Contact information</strong> — name, email address, phone number,
                  company name, and any message you submit through our contact form.
                </li>
                <li>
                  <strong className="text-gray-800">Business information</strong> — details about your business,
                  marketing goals, and service preferences shared during consultations or project discussions.
                </li>
                <li>
                  <strong className="text-gray-800">Technical data</strong> — IP address, browser type, device
                  information, pages visited, referring URLs, and general usage data collected through cookies and
                  analytics tools.
                </li>
                <li>
                  <strong className="text-gray-800">Client account data</strong> — information required to deliver
                  services, such as ad account access, website credentials, or campaign assets, when you engage us
                  as a client.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: '3. How We Use Your Information',
          content: (
            <>
              <p>We use collected information to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Respond to enquiries and provide customer support</li>
                <li>Deliver, manage, and improve our digital marketing services</li>
                <li>Send service-related communications and project updates</li>
                <li>Analyse website performance and improve user experience</li>
                <li>Comply with legal obligations and protect our rights</li>
                <li>Send marketing communications where you have given consent or where permitted by law</li>
              </ul>
            </>
          ),
        },
        {
          title: '4. Cookies & Analytics',
          content: (
            <>
              <p>
                Our website may use cookies and similar technologies to remember preferences, understand how visitors
                use the site, and improve performance. We may use third-party analytics services (such as Google
                Analytics) that collect aggregated, non-personally identifiable usage data.
              </p>
              <p>
                You can control cookies through your browser settings. Disabling cookies may affect certain website
                features.
              </p>
            </>
          ),
        },
        {
          title: '5. How We Share Information',
          content: (
            <>
              <p>We do not sell your personal information. We may share information only in these cases:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong className="text-gray-800">Service providers</strong> — trusted vendors who help us operate
                  our website, analytics, email, hosting, or advertising platforms, under confidentiality obligations.
                </li>
                <li>
                  <strong className="text-gray-800">Legal requirements</strong> — when required by law, court order,
                  or to protect the rights, safety, and security of Blue Globe Media, our clients, or others.
                </li>
                <li>
                  <strong className="text-gray-800">Business transfers</strong> — in connection with a merger,
                  acquisition, or sale of assets, with appropriate notice where required.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: '6. Data Retention',
          content: (
            <p>
              We retain personal information only as long as necessary to fulfil the purposes described in this
              policy, provide services, resolve disputes, enforce agreements, and comply with legal requirements.
              Enquiry data is typically retained for up to 24 months unless a longer period is needed for an active
              client relationship.
            </p>
          ),
        },
        {
          title: '7. Data Security',
          content: (
            <p>
              We implement reasonable administrative, technical, and organisational measures to protect your information
              against unauthorised access, loss, misuse, or alteration. However, no method of transmission over the
              internet or electronic storage is completely secure, and we cannot guarantee absolute security.
            </p>
          ),
        },
        {
          title: '8. Your Rights',
          content: (
            <>
              <p>Depending on applicable law, you may have the right to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate or incomplete information</li>
                <li>Request deletion of your information, subject to legal exceptions</li>
                <li>Withdraw consent for marketing communications at any time</li>
                <li>Object to or restrict certain processing activities</li>
              </ul>
              <p>
                To exercise these rights, email us at{' '}
                <a href={`mailto:${EMAIL}`} className="text-brand hover:underline">{EMAIL}</a>. We will respond
                within a reasonable timeframe.
              </p>
            </>
          ),
        },
        {
          title: '9. Third-Party Links',
          content: (
            <p>
              Our website may contain links to third-party websites or platforms (such as social media networks or
              advertising tools). We are not responsible for the privacy practices of those sites. We encourage you to
              review their privacy policies before providing any personal information.
            </p>
          ),
        },
        {
          title: '10. Children\'s Privacy',
          content: (
            <p>
              Our website and services are not directed at individuals under the age of 18. We do not knowingly
              collect personal information from children. If you believe a child has provided us with personal data,
              please contact us and we will take steps to delete it.
            </p>
          ),
        },
        {
          title: '11. Changes to This Policy',
          content: (
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page with a revised
              &quot;Last updated&quot; date. Continued use of our website after changes are posted constitutes your
              acceptance of the updated policy.
            </p>
          ),
        },
        {
          title: '12. Contact Us',
          content: (
            <p>
              For privacy-related questions or requests, contact Blue Globe Media at{' '}
              <a href={`mailto:${EMAIL}`} className="text-brand hover:underline">{EMAIL}</a> or visit{' '}
              <a href={`${SITE}/contact`} className="text-brand hover:underline">{SITE}/contact</a>.
            </p>
          ),
        },
      ]}
    />
  );
}
